import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { Link } from '@treams/treams-routes';

import useAuthenticatedUser from 'packages/kakapo-ui-application/hooks/useAuthenticatedUser';
import { PageTitle, SubSectionTitle } from 'packages/kakapo-ui-kit/components/common/Headings';
import HorizontalRule from 'packages/kakapo-ui-kit/components/common/HorizontalRule';

import { InboxManagerTableCell, InboxManagerTableRow } from './InboxManagerTable';
import Button, { ButtonSize } from 'packages/kakapo-ui-kit/components/editors/Button';
import CommentIcon from 'packages/kakapo-ui-kit/assets/icons/CommentIcon';
import { ParagraphSmall } from 'packages/kakapo-ui-kit/components/common/Paragraph';
import FeedbackIcon from 'packages/kakapo-ui-kit/assets/icons/FeedbackIcon';
import GoalIcon from 'packages/kakapo-ui-kit/assets/icons/GoalIcon';
import SearchIcon from 'packages/kakapo-ui-kit/assets/icons/SearchIcon';
import { PrimaryButtonLink } from 'packages/kakapo-ui-kit/components/buttons/PrimaryButton';
import { ButtonSize as PrimaryButtonSize } from 'packages/kakapo-ui-kit/types/Button';
import useFeature from 'packages/kakapo-ui-application/hooks/useFeature';

const ActionHolder = styled(Box)`
	flex: 1;
	flex-basis: 1;
	flex-grow: 1;
	flex-shrink: 1;
	margin: 8px;
	min-width: 120px;
`;

const ActionContainer = styled(Flex)`
	border-radius: 5px;
	background: white;
	box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.13);
	cursor: pointer;
	transition: 0.4s ease-out box-shadow;
	height: 100%;
	justify-content: center;

	&:hover {
		box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.25);
	}
`;

const ActionIcon = styled(Flex)`
	font-size: 32px;
	width: 32px;
	height: 32px;
	line-height: 32px;
	align-items: center;
	justify-content: center;

	svg {
		display: block;
		transform: scale(120%, 120%);
	}
`;

function Action(props: { icon: ReactNode; title: string; link: string }) {
	return (
		<ActionHolder flexBasis={0} flexGrow={1}>
			<Link to={props.link}>
				<ActionContainer p={3} pt={4} alignItems="center" flexDirection="column">
					<ActionIcon>{props.icon}</ActionIcon>
					<Box mt={3}>
						<ParagraphSmall color="slateBlue" style={{ textAlign: 'center' }}>
							{props.title}
						</ParagraphSmall>
					</Box>
				</ActionContainer>
			</Link>
		</ActionHolder>
	);
}

const SearchFieldContainer = styled(Flex)`
	border-radius: 18px;
	background: white;
	box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.13);
	cursor: pointer;
	transition: 0.4s ease-out box-shadow;

	&:hover {
		box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.25);
	}
`;

export default function InboxManagerHeader(props: {
	sidebar: boolean;
	header?: boolean;
	small?: boolean;
	toggleSidebar: () => void;
}) {
	const { t } = useTranslation();
	const viewer = useAuthenticatedUser();

	const hasConversations = useFeature('2021.evaluations');
	const hasCompliments = useFeature('2021.compliments');
	const hasFeedback = useFeature('2021.feedback');
	const hasTeamGoals = useFeature('2021.teamGoals');

	return (
		<Box pt={24} pl={4} pr={3}>
			{props.header !== false && (
				<>
					<Box mt={2}>
						<Flex>
							<Box mb={2} flexGrow={1} flexShrink={1}>
								<PageTitle>{t('inbox.title', { name: viewer.firstName })}</PageTitle>
							</Box>
						</Flex>
						<HorizontalRule />
					</Box>

					<Flex mt={4} alignItems="center">
						<Box display={['none', 'none', 'block']}>
							<SubSectionTitle>{t('managerInbox.managerTitle')}</SubSectionTitle>
						</Box>
						<Box flexGrow={1} display={['none', 'none', 'block']} />
						<Box flexShrink={0} flexGrow={0} display={['none', 'none', 'block']} mr={3}>
							<Link to="/search?q=">
								<SearchFieldContainer py={2} px={3} alignItems="center">
									<SearchIcon />
									<Box ml={2}>
										<ParagraphSmall color="slateBlue">
											{t('managerInbox.employeeSearchPlaceholder')}
										</ParagraphSmall>
									</Box>
								</SearchFieldContainer>
							</Link>
						</Box>
						<Box flexShrink={0} flexGrow={[1, 1, 0]}>
							<PrimaryButtonLink
								to="/inbox/personal"
								size={PrimaryButtonSize.Small}
								style={{ height: 36, lineHeight: '24px', display: 'block', textAlign: 'center' }}
							>
								{t('managerInbox.personalInboxButton.title')}
							</PrimaryButtonLink>
						</Box>
					</Flex>
				</>
			)}

			<Flex
				mt={3}
				alignItems="stretch"
				justifyContent="stretch"
				flexWrap="wrap"
				style={{ margin: '-8px', marginTop: 0 }}
			>
				{hasConversations && (
					<Action
						icon={<CommentIcon color="slateBlue" />}
						title={t('managerInbox.actions.startConversation.title')}
						link={`/conversations/request`}
					/>
				)}
				{hasCompliments && (
					<Action
						icon="🎁"
						title={t('managerInbox.actions.giveCompliment.title')}
						link="/feedback/compliments/new"
					/>
				)}
				{hasFeedback && (
					<Action
						icon={<FeedbackIcon color="slateBlue" />}
						title={t('managerInbox.actions.askFeedback.title')}
						link={`/user/${viewer.id}/feedback-v2/request-from-moment`}
					/>
				)}
				{hasTeamGoals && (
					<Action
						icon={<GoalIcon color="slateBlue" />}
						title={t('managerInbox.actions.addTeamGoal.title')}
						link="/group-goals/create"
					/>
				)}
			</Flex>

			{!props.small && (
				<>
					<Flex mt={5} alignItems="center">
						<Box>
							<SubSectionTitle>{t('managerInbox.team.title')}</SubSectionTitle>
						</Box>
						<Box ml={2} flexGrow={0} flexShrink={0}>
							<Button size={ButtonSize.Medium} onClick={() => props.toggleSidebar()}>
								{props.sidebar
									? t('managerInbox.hideFiltersButton.title')
									: t('managerInbox.showFiltersButton.title')}
							</Button>
						</Box>
					</Flex>

					<Box pt={2}>
						<InboxManagerTableRow header>
							<InboxManagerTableCell small />
							<InboxManagerTableCell>{t('managerInbox.table.header.name')}</InboxManagerTableCell>
							<InboxManagerTableCell>
								{t('managerInbox.table.header.jobPosition')}
							</InboxManagerTableCell>
							<InboxManagerTableCell style={{ flexGrow: 1 }}>
								{t('managerInbox.table.header.status')}
							</InboxManagerTableCell>
						</InboxManagerTableRow>
					</Box>
				</>
			)}
		</Box>
	);
}
